import { useEffect } from 'react'

/**
 * Единый пресет для фоновых частиц — плотный и интерактивный, как на
 * RealColibri.com.  Раньше каждый блок лендинга крутил свой particles.js
 * одновременно (O(n²) на кадр) — несколько canvas'ов разом дико лагали.
 *
 * Теперь инстанс создаётся только когда секция реально видна (см. хук) и
 * уничтожается при уходе из вьюпорта — работает максимум ОДИН canvas.
 * Поэтому можно позволить больше частиц + hover/click-интерактивность:
 *  - hover-grab — линии тянутся к курсору,
 *  - click — добавляет частицы,
 *  - retina_detect выключен (иначе рисуем в 2× пикселей на hidpi).
 */
export const PARTICLES_PRESET = {
    particles: {
        // density выключен: иначе particles.js масштабирует число под площадь
        // canvas и реально показывает меньше точек. Хотим ровно `value`.
        number: { value: 200, density: { enable: false } },
        color: { value: '#ffffff' },
        shape: { type: 'circle' },
        opacity: { value: 0.5, random: true },
        size: { value: 2.5, random: true },
        line_linked: {
            enable: true,
            distance: 130,
            color: '#ffffff',
            opacity: 0.35,
            width: 1,
        },
        move: {
            enable: true,
            speed: 1.6,
            direction: 'none',
            random: false,
            straight: false,
            out_mode: 'out',
        },
    },
    interactivity: {
        // 'canvas' (не 'window'): при 'window' particles.js считает курсор
        // от угла вьюпорта, а canvas лежит ниже — реакция уезжает от курсора.
        // 'canvas' меряет offsetX/offsetY относительно самого canvas — точно.
        // Canvas под контентом (z-index 0), так что hover работает на фоне.
        detect_on: 'canvas',
        events: {
            // только grab — линии тянутся к курсору, без раздувания точек.
            onhover: { enable: true, mode: 'grab' },
            // click-интерактивность отключена — лишний эффект.
            onclick: { enable: false, mode: 'push' },
            // resize обрабатываем сами в хуке (полный пересоздаём инстанс),
            // встроенный пересчёт particles.js выключен.
            resize: false,
        },
        modes: {
            grab: { distance: 180, line_linked: { opacity: 1 } },
            push: { particles_nb: 4 },
        },
    },
    retina_detect: false,
}

/** Уничтожает particles.js-инстанс, привязанный к canvas внутри `#id`. */
export const destroyParticles = (id: string) => {
    const doms = window.pJSDom
    if (!Array.isArray(doms)) {
        // particles.js баг: destroypJS() обнуляет ВЕСЬ общий window.pJSDom
        // (а не только свою запись). Чиним, чтобы следующий particlesJS()
        // не упал на null.push().
        window.pJSDom = []
        return
    }
    for (let i = doms.length - 1; i >= 0; i--) {
        const el = doms[i]?.pJS?.canvas?.el
        if (el && el.parentElement?.id === id) {
            try {
                doms[i]?.pJS?.fn?.vendors?.destroypJS?.()
            } catch {
                /* noop */
            }
            doms.splice(i, 1)
        }
    }
    // destroypJS() внутри обнуляет window.pJSDom — `doms` всё ещё указывает
    // на исходный массив, восстанавливаем глобал из него.
    window.pJSDom = doms
}

/**
 * Запускает фоновые частицы в элементе `#id` на всё время жизни секции.
 * Инстанс создаётся при монтировании и уничтожается при размонтировании.
 *
 * При ресайзе окна инстанс полностью пересоздаётся (с дебаунсом): сначала
 * уничтожаем старый canvas, потом поднимаем новый под актуальный размер —
 * так не копятся обработчики/canvas'ы и нет лишней нагрузки во время тянучки.
 */
const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Ширина, ниже которой режем число частиц (mobile). */
const MOBILE_BREAKPOINT = 768
/** Во сколько раз меньше частиц на мобильных. */
const MOBILE_PARTICLE_RATIO = 0.2

/**
 * Возвращает config с уменьшенным числом частиц на узких экранах —
 * на мобильных их слишком много (визуальный шум + нагрузка).
 */
const responsiveConfig = (config: any): object => {
    const isMobile =
        typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
    const baseValue = config?.particles?.number?.value

    // Пользователь просил меньше движения — рисуем неподвижный узор.
    if (prefersReducedMotion()) {
        config = {
            ...config,
            particles: {
                ...config?.particles,
                move: { ...config?.particles?.move, enable: false },
            },
        }
    }

    if (!isMobile || typeof baseValue !== 'number') return config

    return {
        ...config,
        particles: {
            ...config.particles,
            number: {
                ...config.particles.number,
                value: Math.max(15, Math.round(baseValue * MOBILE_PARTICLE_RATIO)),
            },
        },
    }
}

export const useParticlesBackground = (
    id: string,
    config: object = PARTICLES_PRESET
) => {
    useEffect(() => {
        const target = document.getElementById(id)
        if (!target || typeof window.particlesJS !== 'function') return

        // Работает ли сейчас canvas этой секции.
        let running = false
        // Видна ли секция.  Без IntersectionObserver считаем, что видна.
        let visible = typeof IntersectionObserver === 'undefined'

        const start = () => {
            if (running) return
            // Гард: particles.js пушит в window.pJSDom, который их же
            // destroypJS() мог обнулить. Без этого — null.push() и краш.
            if (!Array.isArray(window.pJSDom)) window.pJSDom = []
            try {
                // Пересчитываем под текущую ширину — на ресайзе start()
                // вызывается заново, так что breakpoint всегда актуальный.
                window.particlesJS(id, responsiveConfig(config))
                running = true
            } catch {
                /* particles — это декор, не валим из-за него React-дерево */
            }
        }

        const stop = () => {
            if (!running) return
            destroyParticles(id)
            running = false
        }

        // Canvas живёт, только пока секция в кадре (с небольшим запасом):
        // на лендинге четыре таких фона, и считать все сразу на каждом кадре
        // незачем — на телефонах это давало рывки при прокрутке.
        let observer: IntersectionObserver | null = null
        if (typeof IntersectionObserver !== 'undefined') {
            observer = new IntersectionObserver(
                entries => {
                    visible = entries[entries.length - 1].isIntersecting
                    if (visible) start()
                    else stop()
                },
                { rootMargin: '200px 0px' }
            )
            observer.observe(target)
        } else {
            start()
        }

        let resizeTimer: ReturnType<typeof setTimeout> | null = null
        // На телефонах resize приходит и при прокрутке (прячется адресная
        // строка, меняется только высота) — пересоздавать canvas из-за этого
        // не нужно, частицы «перепрыгивали».
        let lastWidth = window.innerWidth
        const handleResize = () => {
            if (window.innerWidth === lastWidth) return
            lastWidth = window.innerWidth
            if (resizeTimer) clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => {
                stop()
                if (visible) start()
            }, 200)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeTimer) clearTimeout(resizeTimer)
            observer?.disconnect()
            stop()
        }
    }, [id])
}
